import React, { useEffect, useMemo, useState } from 'react';
import { Company, Lead, Opportunity, User } from '../types';
import { api } from '../services/apiService';
import OpportunityPDFModal from '../components/OpportunityPDFModal';

interface OpportunitiesViewProps {
  user: User;
  company: Company;
}

type OpportunityFormItem = { label: string; quantity: number; unitPrice: number };

const currencySymbol = (code?: string) => {
  switch (code) { case 'USD': return '$'; case 'GBP': return '£'; case 'CHF': return 'CHF'; default: return '€'; }
};

// ---- Formulaire (création + édition) ----
const OppForm: React.FC<{
  title: string;
  leads: Lead[];
  formLeadId: string; setFormLeadId: (v: string) => void;
  formClientCompanyName: string; setFormClientCompanyName: (v: string) => void;
  formSubject: string; setFormSubject: (v: string) => void;
  formComment: string; setFormComment: (v: string) => void;
  formItems: OpportunityFormItem[]; setFormItems: React.Dispatch<React.SetStateAction<OpportunityFormItem[]>>;
  isEditing: boolean;
  isSubmitting: boolean;
  symbol: string;
  onSubmit: (e: React.FormEvent) => void;
  onCancel: () => void;
}> = ({ title, leads, formLeadId, setFormLeadId, formClientCompanyName, setFormClientCompanyName,
        formSubject, setFormSubject, formComment, setFormComment,
        formItems, setFormItems, isEditing, isSubmitting, symbol, onSubmit, onCancel }) => {

  const total = formItems.reduce((s, i) => s + Number(i.quantity) * Number(i.unitPrice), 0);

  const updateLine = (idx: number, patch: Partial<OpportunityFormItem>) =>
    setFormItems(prev => prev.map((it, i) => i === idx ? { ...it, ...patch } : it));

  const addLine = () => setFormItems(prev => [...prev, { label: '', quantity: 1, unitPrice: 0 }]);
  const removeLine = (idx: number) => setFormItems(prev => prev.filter((_, i) => i !== idx));

  const onSelectLead = (leadId: string) => {
    setFormLeadId(leadId);
    const lead = leads.find(l => String(l.id) === String(leadId));
    if (lead) setFormClientCompanyName(lead.clientCompanyName || '');
  };

  return (
    <div className="orion-modal-backdrop fixed inset-0 z-[9999] flex items-center justify-center p-4 backdrop-blur-sm animate-in fade-in">
      <div className="orion-modal-panel w-full max-w-3xl rounded-2xl shadow-2xl p-4 sm:p-8 border animate-in zoom-in-95 max-h-[90vh] overflow-y-auto custom-scrollbar">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-black text-[#e0eaff]">{title}</h3>
          <button type="button" title="Fermer" onClick={onCancel} className="p-2 rounded-lg text-[#546a96] hover:text-rose-400 hover:bg-rose-500/10 transition-all">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {!isEditing && (
              <div>
                <label className="text-[10px] font-black text-[#546a96] uppercase tracking-widest mb-1.5 block">Prospect lié *</label>
                <select value={formLeadId} onChange={e => onSelectLead(e.target.value)} required title="Prospect lié"
                  className="orion-modal-input w-full rounded-xl px-4 py-3 text-[13px] font-bold outline-none focus:ring-2 focus:ring-[rgba(17,197,214,0.4)]">
                  <option value="">Sélectionner...</option>
                  {leads.map(l => <option key={l.id} value={l.id}>{(l.clientCompanyName || 'Client')} — {l.name}</option>)}
                </select>
              </div>
            )}
            <div>
              <label className="text-[10px] font-black text-[#546a96] uppercase tracking-widest mb-1.5 block">Nom du client *</label>
              <input type="text" required placeholder="Nom du client" value={formClientCompanyName} onChange={e => setFormClientCompanyName(e.target.value)}
                className="orion-modal-input w-full rounded-xl px-4 py-3 text-[13px] font-bold outline-none focus:ring-2 focus:ring-[rgba(17,197,214,0.4)]" />
            </div>
            <div className={isEditing ? 'md:col-span-2' : ''}>
              <label className="text-[10px] font-black text-[#546a96] uppercase tracking-widest mb-1.5 block">Objet de la proposition *</label>
              <input type="text" required value={formSubject} onChange={e => setFormSubject(e.target.value)}
                placeholder="Ex : Abonnement maintenance annuelle"
                className="orion-modal-input w-full rounded-xl px-4 py-3 text-[13px] font-bold outline-none focus:ring-2 focus:ring-[rgba(17,197,214,0.4)]" />
            </div>
          </div>

          <div>
            <label className="text-[10px] font-black text-[#546a96] uppercase tracking-widest mb-1.5 block">Commentaire</label>
            <textarea value={formComment} onChange={e => setFormComment(e.target.value)} placeholder="Notes, conditions..."
              className="orion-modal-input w-full rounded-xl px-4 py-3 text-[13px] font-medium outline-none focus:ring-2 focus:ring-[rgba(17,197,214,0.4)] h-20 resize-none" />
          </div>

          {/* Lignes produits */}
          <div className="rounded-xl border border-[rgba(100,140,210,0.2)] bg-[rgba(100,140,210,0.04)] p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-[10px] font-black text-[#546a96] uppercase tracking-widest">Produits / Services</span>
              <button type="button" onClick={addLine} className="text-[11px] font-black text-[#11c5d6] hover:text-[#0fa8b8] flex items-center gap-1 transition-colors">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" /></svg>
                Ajouter ligne
              </button>
            </div>

            {/* En-têtes colonnes — masqués sur mobile */}
            <div className="hidden sm:grid grid-cols-12 gap-2 mb-1.5 px-1">
              <span className="col-span-5 text-[9px] font-black text-[#546a96] uppercase tracking-widest">Désignation</span>
              <span className="col-span-2 text-[9px] font-black text-[#546a96] uppercase tracking-widest text-center">Qté</span>
              <span className="col-span-2 text-[9px] font-black text-[#546a96] uppercase tracking-widest text-center">P.U.</span>
              <span className="col-span-2 text-[9px] font-black text-[#546a96] uppercase tracking-widest text-right">Total</span>
              <span className="col-span-1"></span>
            </div>

            <div className="space-y-3">
              {formItems.map((line, idx) => (
                <div key={idx} className="flex flex-col sm:grid sm:grid-cols-12 gap-2 sm:items-center p-2 sm:p-0 rounded-lg sm:rounded-none border border-[rgba(100,140,210,0.15)] sm:border-0">
                  <input type="text" value={line.label} onChange={e => updateLine(idx, { label: e.target.value })}
                    placeholder="Produit / service"
                    className="sm:col-span-5 orion-modal-input rounded-lg px-3 py-2 text-[12px] font-bold outline-none focus:ring-1 focus:ring-[rgba(17,197,214,0.4)]" />
                  <div className="flex gap-2 sm:contents">
                    <input type="number" step="1" min="1" title="Quantité" placeholder="Qté" value={line.quantity} onChange={e => updateLine(idx, { quantity: Number(e.target.value) })}
                      className="flex-1 sm:col-span-2 orion-modal-input rounded-lg px-3 py-2 text-[12px] font-bold outline-none text-center focus:ring-1 focus:ring-[rgba(17,197,214,0.4)]" />
                    <input type="number" step="0.01" min="0" title="Prix unitaire" placeholder="P.U." value={line.unitPrice} onChange={e => updateLine(idx, { unitPrice: Number(e.target.value) })}
                      className="flex-1 sm:col-span-2 orion-modal-input rounded-lg px-3 py-2 text-[12px] font-bold outline-none text-center focus:ring-1 focus:ring-[rgba(17,197,214,0.4)]" />
                    <div className="sm:col-span-2 text-[12px] font-black text-[#11c5d6] flex items-center justify-end sm:justify-end pr-1 min-w-[60px]">
                      {(Number(line.quantity) * Number(line.unitPrice)).toFixed(2)} {symbol}
                    </div>
                    <button type="button" title="Supprimer la ligne" onClick={() => removeLine(idx)} disabled={formItems.length === 1}
                      className="sm:col-span-1 flex items-center justify-center p-1.5 rounded-lg text-[#546a96] hover:text-rose-400 hover:bg-rose-500/10 disabled:opacity-20 transition-all">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-4 flex items-center justify-end gap-2 border-t border-[rgba(100,140,210,0.15)] pt-3">
              <span className="text-[10px] font-black text-[#546a96] uppercase tracking-widest">Total</span>
              <span className="text-[18px] font-black text-[#11c5d6]">{total.toFixed(2)} {symbol}</span>
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onCancel}
              className="flex-1 py-3 rounded-xl text-[12px] font-bold text-[#546a96] hover:text-[#7f93bc] bg-[rgba(100,140,210,0.06)] hover:bg-[rgba(100,140,210,0.1)] transition-all">
              Annuler
            </button>
            <button type="submit" disabled={isSubmitting}
              className="orion-modal-primary-btn flex-1 py-3 rounded-xl text-[12px] font-bold disabled:opacity-50">
              {isSubmitting ? 'Enregistrement...' : isEditing ? 'Enregistrer' : 'Créer l\'opportunité'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ---- Vue principale ----
const OpportunitiesView: React.FC<OpportunitiesViewProps> = ({ user, company }) => {
  const [loading, setLoading] = useState(true);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingOpp, setEditingOpp] = useState<Opportunity | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [pdfOpportunity, setPdfOpportunity] = useState<Opportunity | null>(null);
  const [confirmDeleteOppId, setConfirmDeleteOppId] = useState<string | null>(null);

  const [formLeadId, setFormLeadId] = useState('');
  const [formClientCompanyName, setFormClientCompanyName] = useState('');
  const [formSubject, setFormSubject] = useState('');
  const [formComment, setFormComment] = useState('');
  const [formItems, setFormItems] = useState<OpportunityFormItem[]>([{ label: '', quantity: 1, unitPrice: 0 }]);

  const symbol = currencySymbol(company.currency);

  const loadData = async () => {
    setLoading(true);
    try {
      const [leadData, oppData] = await Promise.all([
        api.getLeads({ companyId: company.id, userId: user.id, role: user.role }),
        api.getOpportunities({ companyId: company.id })
      ]);
      setLeads(leadData || []);
      setOpportunities(oppData || []);
    } catch (e) { console.error('Erreur chargement opportunites', e); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;

    const fetchData = async (attempt = 0) => {
      if (cancelled) return;
      setLoading(true);
      try {
        const [leadData, oppData] = await Promise.all([
          api.getLeads({ companyId: company.id, userId: user.id, role: user.role }),
          api.getOpportunities({ companyId: company.id })
        ]);
        if (cancelled) return;
        setLeads(leadData || []);
        setOpportunities(oppData || []);
      } catch (e) {
        console.error('Erreur chargement opportunites', e);
        if (attempt === 0 && !cancelled) {
          retryTimer = setTimeout(() => { void fetchData(1); }, 400);
          return;
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void fetchData();
    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [company.id, user.id, user.role]);

  const filteredOpportunities = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return opportunities;
    return opportunities.filter(o =>
      o.clientCompanyName.toLowerCase().includes(q) ||
      o.subject.toLowerCase().includes(q) ||
      (o.comment || '').toLowerCase().includes(q)
    );
  }, [opportunities, searchTerm]);

  const [oppPage, setOppPage] = useState(1);
  const oppPerPage = 25;
  const oppTotalPages = Math.ceil(filteredOpportunities.length / oppPerPage);
  const paginatedOpportunities = useMemo(() => {
    const start = (oppPage - 1) * oppPerPage;
    return filteredOpportunities.slice(start, start + oppPerPage);
  }, [filteredOpportunities, oppPage]);
  useEffect(() => { setOppPage(1); }, [searchTerm]);

  const resetForm = () => {
    setFormLeadId(''); setFormClientCompanyName(''); setFormSubject('');
    setFormComment(''); setFormItems([{ label: '', quantity: 1, unitPrice: 0 }]);
  };

  const openCreate = () => { resetForm(); setShowCreateModal(true); };
  const openEdit = (opp: Opportunity) => {
    setEditingOpp(opp);
    setFormLeadId(opp.leadId || '');
    setFormClientCompanyName(opp.clientCompanyName);
    setFormSubject(opp.subject);
    setFormComment(opp.comment || '');
    setFormItems(opp.items.map(i => ({ label: i.label, quantity: i.quantity, unitPrice: i.unitPrice })));
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const created = await api.createOpportunity({
        companyId: company.id, leadId: formLeadId,
        clientCompanyName: formClientCompanyName.trim(),
        subject: formSubject.trim(), comment: formComment.trim(),
        createdBy: user.id,
        items: formItems.map(i => ({ label: i.label.trim(), quantity: Number(i.quantity), unitPrice: Number(i.unitPrice) }))
      }, { id: user.id, name: user.name });
      setOpportunities(prev => [created, ...prev]);
      setShowCreateModal(false); resetForm();
    } catch (err) { console.error('Erreur creation opportunite', err); }
    finally { setIsSubmitting(false); }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingOpp) return;
    setIsSubmitting(true);
    try {
      await api.updateOpportunity(editingOpp.id, {
        subject: formSubject.trim(), comment: formComment.trim(),
        clientCompanyName: formClientCompanyName.trim(),
        items: formItems.map(i => ({
          id: 'oppi_' + Date.now() + '_' + Math.random().toString(36).substr(2, 5),
          opportunityId: editingOpp.id,
          label: i.label.trim(), quantity: Number(i.quantity),
          unitPrice: Number(i.unitPrice),
          totalPrice: Number(i.quantity) * Number(i.unitPrice)
        }))
      });
      await loadData();
      setEditingOpp(null); resetForm();
    } catch (err) { console.error('Erreur update opportunite', err); }
    finally { setIsSubmitting(false); }
  };

  const handleDelete = async (e: React.MouseEvent, oppId: string) => {
    e.stopPropagation();
    try {
      await api.deleteOpportunity(oppId);
      setOpportunities(prev => prev.filter(o => o.id !== oppId));
      setConfirmDeleteOppId(null);
    } catch (err) { console.error('Erreur suppression opportunite', err); }
  };

  return (
    <div className="orion-dashboard space-y-4 animate-in fade-in duration-500 flex-1 min-h-0 flex flex-col">

      {/* Toolbar */}
      <div className="orion-toolbar flex flex-col md:flex-row justify-between items-center gap-3 p-1.5 rounded-xl border flex-shrink-0">
        <div className="relative w-full md:w-80">
          <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#546a96]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input type="text" placeholder="Rechercher client, objet..."
            value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-[rgba(100,140,210,0.06)] border border-[rgba(100,140,210,0.15)] rounded-lg text-[13px] font-bold text-[#c8daf5] placeholder-[#546a96] outline-none focus:border-[rgba(17,197,214,0.4)] transition-all" />
        </div>
        <button type="button" onClick={openCreate} className="orion-primary-btn text-white px-4 py-2.5 rounded-lg text-[11px] font-black flex items-center gap-2">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4" /></svg>
          Nouvelle opportunité
        </button>
      </div>

      {/* Table */}
      <div className="orion-table-shell flex-1 rounded-xl border overflow-hidden flex flex-col">
        <div className="overflow-x-auto custom-scrollbar flex-1">
          <table className="orion-resp-table w-full text-left border-collapse">
            <thead className="orion-table-head sticky top-0 z-10">
              <tr className="text-[10px] font-black uppercase tracking-widest">
                <th className="px-5 py-4">Client</th>
                <th className="px-5 py-4 resp-hide">Objet</th>
                <th className="px-5 py-4 resp-hide text-center w-20">Lignes</th>
                <th className="px-5 py-4">Montant</th>
                <th className="px-5 py-4 resp-hide">Date</th>
                <th className="px-5 py-4 text-right w-36">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={6} className="text-center py-12 text-[#546a96] text-xs font-bold animate-pulse">Chargement...</td></tr>
              )}
              {!loading && paginatedOpportunities.map(opp => (
                <tr
                  key={opp.id}
                  onClick={() => openEdit(opp)}
                  className="orion-table-row-hover group transition-colors cursor-pointer border-b border-[rgba(100,140,210,0.06)]"
                >
                  {/* Client */}
                  <td className="px-5 py-3.5">
                    <div className="text-[13px] font-bold text-slate-900">{opp.clientCompanyName}</div>
                    {opp.comment && <div className="text-[11px] text-slate-500 mt-0.5 truncate max-w-[180px]">{opp.comment}</div>}
                  </td>

                  {/* Objet */}
                  <td className="resp-hide px-5 py-3.5 text-[12px] font-bold text-slate-700 max-w-[200px]">
                    <span className="truncate block">{opp.subject}</span>
                  </td>

                  {/* Nb lignes */}
                  <td className="resp-hide px-5 py-3.5 text-center">
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-[rgba(100,140,210,0.12)] text-[11px] font-black text-[#7f93bc]">
                      {opp.items.length}
                    </span>
                  </td>

                  {/* Montant */}
                  <td className="px-5 py-3.5">
                    <span className="text-[13px] font-black text-[#11c5d6]">
                      {opp.totalAmount.toFixed(2)} {symbol}
                    </span>
                  </td>

                  {/* Date */}
                  <td className="resp-hide px-5 py-3.5 text-[11px] font-bold text-slate-500">
                    {new Date(opp.createdAt).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' })}
                  </td>

                  {/* Actions */}
                  <td className="px-5 py-3.5" onClick={e => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-1">
                      {/* PDF */}
                      <button type="button" title="Aperçu PDF"
                        onClick={e => { e.stopPropagation(); setPdfOpportunity(opp); }}
                        className="p-2 rounded-lg text-[#546a96] hover:text-[#11c5d6] hover:bg-[rgba(17,197,214,0.1)] transition-all">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                        </svg>
                      </button>

                      {/* Modifier */}
                      <button type="button" title="Modifier"
                        onClick={e => { e.stopPropagation(); openEdit(opp); }}
                        className="p-2 rounded-lg text-[#546a96] hover:text-[#11c5d6] hover:bg-[rgba(17,197,214,0.1)] transition-all">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>

                      {/* Supprimer */}
                      {confirmDeleteOppId === opp.id ? (
                        <div className="flex items-center gap-1 animate-in fade-in slide-in-from-right-2">
                          <span className="text-[9px] font-black text-rose-400 uppercase">Sûr ?</span>
                          <button type="button" onClick={e => handleDelete(e, opp.id)} className="px-2 py-1 rounded-md text-[10px] font-black bg-rose-500 text-white hover:bg-rose-600">Oui</button>
                          <button type="button" onClick={e => { e.stopPropagation(); setConfirmDeleteOppId(null); }} className="px-2 py-1 rounded-md text-[10px] font-black bg-[rgba(100,140,210,0.1)] text-[#7f93bc] hover:bg-[rgba(100,140,210,0.2)]">Non</button>
                        </div>
                      ) : (
                        <button type="button" title="Supprimer"
                          onClick={e => { e.stopPropagation(); setConfirmDeleteOppId(opp.id); }}
                          className="p-2 rounded-lg text-[#546a96] hover:text-rose-400 hover:bg-rose-500/10 transition-all">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {!loading && filteredOpportunities.length === 0 && (
            <div className="text-center py-16 text-[#546a96] text-xs font-bold">Aucune opportunité trouvée.</div>
          )}
        </div>

        {/* Pagination */}
        {oppTotalPages > 1 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-[rgba(100,140,210,0.1)]">
            <span className="text-[11px] font-bold text-[#546a96]">{((oppPage - 1) * oppPerPage) + 1}–{Math.min(oppPage * oppPerPage, filteredOpportunities.length)} sur {filteredOpportunities.length}</span>
            <div className="flex items-center gap-1">
              <button type="button" onClick={() => setOppPage(1)} disabled={oppPage === 1} className="px-2 py-1 rounded-lg text-[11px] font-bold text-[#546a96] hover:bg-[rgba(100,140,210,0.1)] disabled:opacity-30">«</button>
              <button type="button" onClick={() => setOppPage(p => Math.max(1, p - 1))} disabled={oppPage === 1} className="px-2 py-1 rounded-lg text-[11px] font-bold text-[#546a96] hover:bg-[rgba(100,140,210,0.1)] disabled:opacity-30">‹</button>
              {Array.from({ length: Math.min(5, oppTotalPages) }, (_, i) => {
                let page: number;
                if (oppTotalPages <= 5) page = i + 1;
                else if (oppPage <= 3) page = i + 1;
                else if (oppPage >= oppTotalPages - 2) page = oppTotalPages - 4 + i;
                else page = oppPage - 2 + i;
                return (
                  <button type="button" key={page} onClick={() => setOppPage(page)}
                    className={`w-7 h-7 rounded-lg text-[11px] font-black transition-all ${oppPage === page ? 'bg-[rgba(17,197,214,0.2)] text-[#11c5d6]' : 'text-[#546a96] hover:bg-[rgba(100,140,210,0.1)]'}`}>
                    {page}
                  </button>
                );
              })}
              <button type="button" onClick={() => setOppPage(p => Math.min(oppTotalPages, p + 1))} disabled={oppPage === oppTotalPages} className="px-2 py-1 rounded-lg text-[11px] font-bold text-[#546a96] hover:bg-[rgba(100,140,210,0.1)] disabled:opacity-30">›</button>
              <button type="button" onClick={() => setOppPage(oppTotalPages)} disabled={oppPage === oppTotalPages} className="px-2 py-1 rounded-lg text-[11px] font-bold text-[#546a96] hover:bg-[rgba(100,140,210,0.1)] disabled:opacity-30">»</button>
            </div>
          </div>
        )}
      </div>

      {/* Modal création */}
      {showCreateModal && (
        <OppForm
          title="Nouvelle opportunité"
          leads={leads}
          formLeadId={formLeadId} setFormLeadId={setFormLeadId}
          formClientCompanyName={formClientCompanyName} setFormClientCompanyName={setFormClientCompanyName}
          formSubject={formSubject} setFormSubject={setFormSubject}
          formComment={formComment} setFormComment={setFormComment}
          formItems={formItems} setFormItems={setFormItems}
          isEditing={false}
          isSubmitting={isSubmitting}
          symbol={symbol}
          onSubmit={handleCreate}
          onCancel={() => { setShowCreateModal(false); resetForm(); }}
        />
      )}

      {/* Modal édition */}
      {editingOpp && (
        <OppForm
          title="Modifier l'opportunité"
          leads={leads}
          formLeadId={formLeadId} setFormLeadId={setFormLeadId}
          formClientCompanyName={formClientCompanyName} setFormClientCompanyName={setFormClientCompanyName}
          formSubject={formSubject} setFormSubject={setFormSubject}
          formComment={formComment} setFormComment={setFormComment}
          formItems={formItems} setFormItems={setFormItems}
          isEditing={true}
          isSubmitting={isSubmitting}
          symbol={symbol}
          onSubmit={handleUpdate}
          onCancel={() => { setEditingOpp(null); resetForm(); }}
        />
      )}

      {/* PDF */}
      {pdfOpportunity && (
        <OpportunityPDFModal
          opportunity={pdfOpportunity}
          companyInfo={{ name: company.name, currency: company.currency, address: company.address, email: company.email, phone: company.phone, siret: company.siret }}
          onClose={() => setPdfOpportunity(null)}
        />
      )}
    </div>
  );
};

export default OpportunitiesView;
